import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useBreakpointV2 } from "@tm/ui-widgets";
import { LandingHeaderMobile } from "../../components";
import { Page } from "./Claims.style";
import { ClaimsProvider } from "./ClaimsContext";
import { ClaimErrorBoundary } from "./components/ClaimErrorBoundary";
import { ClaimInfoView } from "./components/ClaimInfoView";
import { ClaimModalProvider } from "./modalManager";
import type {
  ApiComponentEntry,
  ClaimCardBlock,
  ClaimInfoViewParams,
  ClaimLandingNormalized,
  ClaimPageData,
  ClaimsWrapperProps,
  ClaimViewState,
} from "./types";
import { COMPONENT_ID_TO_CARD_NAME } from "./types";

const ClaimsDesktop = dynamic(() => import("./ClaimsDesktop"), { ssr: true });

const ClaimsPWA = dynamic(() => import("./ClaimsPWA"), {
  ssr: true,
});

function ClaimsWrapper({ data }: ClaimsWrapperProps) {
  const [claimData] = useState(data);
  const [activeView, setActiveView] = useState<ClaimViewState>("landing");
  const [infoViewParams, setInfoViewParams] =
    useState<ClaimInfoViewParams | null>(null);

  const refreshClaimData = useCallback(() => {
    // Client-side refresh will be wired when the API is ready
  }, []);

  const navigateToInfo = useCallback((params: ClaimInfoViewParams) => {
    setInfoViewParams(params);
    setActiveView("info");
  }, []);

  const navigateToLanding = useCallback(() => {
    setActiveView("landing");
    setInfoViewParams(null);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeView]);

  const normalizedData: ClaimLandingNormalized = useMemo(() => {
    const headingTitle = (claimData?.["headingTitle"] as string) ?? "";
    const pageData = (claimData?.["pageData"] as ClaimPageData) ?? {};

    const rawComponents =
      (claimData?.["components"] as ApiComponentEntry[]) ?? [];

    const cardList: ClaimCardBlock[] = rawComponents
      .filter(
        (c): c is ApiComponentEntry & { id: string } =>
          c != null && typeof c.id === "string"
      )
      .map((c) => ({
        cardName: COMPONENT_ID_TO_CARD_NAME[c.id] ?? c.id,
        data: c.data,
      }));

    const byCardName: Record<string, unknown> = {};
    for (const card of cardList) {
      if (card?.cardName) byCardName[card.cardName] = card?.data;
    }

    return { headingTitle, pageData, cardList, byCardName, raw: claimData };
  }, [claimData]);

  const ctxValue = useMemo(
    () => ({
      ...normalizedData,
      refreshClaimData,
      activeView,
      infoViewParams,
      navigateToInfo,
      navigateToLanding,
    }),
    [
      normalizedData,
      refreshClaimData,
      activeView,
      infoViewParams,
      navigateToInfo,
      navigateToLanding,
    ]
  );

  const device = useBreakpointV2();
  const isMobile = device === "sm";

  const bookingId = normalizedData.pageData?.bookingId ?? "";
  const currency = normalizedData.pageData?.currency || "INR";

  const mobileHeaderTitle = normalizedData.headingTitle || "Claim Insurance";

  const handleMobileBack =
    activeView === "info" ? navigateToLanding : undefined;

  return (
    <ClaimsProvider value={ctxValue}>
      <ClaimModalProvider>
        <ClaimErrorBoundary onReset={navigateToLanding}>
          {isMobile ? (
            <Page>
              <LandingHeaderMobile
                displayWhiteHeader
                headerTitle={mobileHeaderTitle}
                handleBackBtn={handleMobileBack}
              />
              {activeView === "landing" && (
                <ClaimsPWA normalizedData={normalizedData} />
              )}
              {activeView === "info" && infoViewParams && (
                <ClaimInfoView
                  params={infoViewParams}
                  bookingId={bookingId}
                  currency={currency}
                  onBack={navigateToLanding}
                />
              )}
            </Page>
          ) : (
            <>
              {activeView === "landing" && (
                <ClaimsDesktop normalizedData={normalizedData} />
              )}
              {activeView === "info" && infoViewParams && (
                <ClaimInfoView
                  params={infoViewParams}
                  bookingId={bookingId}
                  currency={currency}
                  onBack={navigateToLanding}
                />
              )}
            </>
          )}
        </ClaimErrorBoundary>
      </ClaimModalProvider>
    </ClaimsProvider>
  );
}

export default ClaimsWrapper;
